/**
 * /app/trusts/$id — Trust detail overview tab.
 *
 * Driven by:
 *   - useTrust(id)               — trust facts (name, registration, deed)
 *   - useBeneficialOwnership(id) — stakeholder counts by category
 *   - useTrustAuditLog(...)      — recent activity, server-scoped to this trust
 *     (QA finding 2026-07-16: this card used to call the tenant-wide
 *     useAuditLog and filter client-side by subjectId, silently
 *     wrong/empty on a multi-trust firm account — see activity-feed.tsx)
 *   - useResolutions({ trustId, status: 'awaiting' }) — outstanding sigs
 *   - useDashboardHoldings(id)   — portfolio value for the hero
 *
 * 2026-06-24 (app polish): the old AUM strip became a brand-coloured HERO
 * (focal point + portfolio value + a one-glance health line + the headline
 * action), keeping the same honest holdings states and the `aum-summary`
 * test hook. Stakeholder counts dropped to a supporting weight (the value is
 * now the dominant number), the deed date shows once (topbar dropped it), and
 * "FICA days out: N" reads as human time-to-review. Warm rolls in via the
 * `.app-warm` class on the shared shell.
 */

import { useState } from 'react';
import { displayLegalName } from '../../../../lib/format/legal-name';
import { displayPersonName } from '../../../../lib/format/person-name';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ScreenShell } from '../../../../components/screens/screen-shell';
import { TrustTabs } from '../../../../components/screens/trust-tabs';
import { Icon } from '../../../../components/icon';
import {
  useTrust,
  useTrusts,
  useTrustSurfaces,
  useCanActOnTrust,
  useBeneficialOwnership,
  useTrustAuditLog,
  useResolutions,
  useTrustMembers,
  useTrustBeneficiaries,
  useCompanies,
  type BoCategory,
  type BeneficialOwnerView,
  type CompanyView,
  type TrustSummary,
} from '../../../../lib/api';
import { useDashboardHoldings, assetClassLabel } from '../../../../lib/api/dashboard';
import { useTrustAssets } from '../../../../lib/api';
import { labelForEvent, labelForEventSubject } from '../../../../lib/labels';
import { useRoleLabel } from '../../../../lib/labels/use-role-label';
import { formatZarCents, formatRand } from '../../../../lib/fmt-r';
import { registrationDisplay } from '../../../../lib/format/registration';
import { nameList } from '../../../../lib/format/name-list';
import { displayTrustName } from '../../../../lib/format/trust-name';
import { mapError } from '../../../../lib/error-messages';
import { TrustKeyFacts } from '../../../../components/trusts/trust-key-facts';
import { StatutoryCalendarCard } from '../../../../components/statutory-calendar-card';
import { formatAsAtShort } from '../../../../components/value-provenance';
import { calendarFicaReviewOn } from '../../../../lib/fica-calendar';
import {
  ficaDaysOutOf,
  ficaVerifiedPhrase,
  initialFicaOutstandingOnRegister,
  ficaReviewDateOf,
  isInitialFicaOutstanding,
} from '../../../../lib/fica-review';
import { useActingContext } from '../../../../lib/acting/context';
import { canRecordFicaCheck } from '../../../../lib/authz/firm-capabilities';
import {
  buildTrustRoster,
  registerOnlyPeople,
  rosterCountPhrasesOf,
  ROLE_GROUP_ORDER,
  ROLE_GROUP_LABEL,
  type RosterRoleCategory,
} from '../../../../lib/trust-roster';
import {
  RecordFicaCheckDialog,
  WithdrawFicaCheckDialog,
} from '../../../../components/trusts/fica-check-dialog';
import { initialsOf } from '../../../../lib/format/initials';

/** Key facts stay dark until the deed extraction has been evaluated against
 *  real deeds. Mirrors the VITE_RESOLUTION_IMPORT_LIVE pattern already in use.
 *  Read at call time, not module load, so tests can stub it. */
function isTrustKeyFactsLive(): boolean {
  return (import.meta.env['VITE_TRUST_KEY_FACTS_LIVE'] as string | undefined) === 'true';
}

export const Route = createFileRoute('/app/trusts/$id/')({
  component: TrustOverviewScreen,
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'Not on file';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = Date.now();
  const delta = now - d.getTime();
  const mins = Math.floor(delta / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

function avatarColorOf(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const palette = ['ink', 'teal-soft', 'brass', ''];
  return palette[Math.abs(h) % palette.length] ?? '';
}

/** Human time-to-FICA-review (replaces the raw "FICA days out: N"). */
function ficaHuman(daysOut: number): string {
  if (daysOut <= 0) return `Overdue by ${Math.abs(daysOut)} days`;
  const months = Math.round(daysOut / 30);
  if (months <= 1) return `~${Math.max(1, months)} month`;
  return `~${months} months`;
}

// ── WHOSE FICA IS ACTUALLY ON FILE (TRS-01 · TRS-02 · TRS-03) ────────────────
//
// Beno, walking two real trusts on 22 August: *"although it says two trusts in
// good standing, it's not correct."* Not one FICA document existed for either.
// The phrase was removed on 22 August; nothing replaced it, so the screen simply
// stopped saying anything at all about the one thing he was asking after.
//
// A trust's FICA position is not a property of the trust. It is a property of
// each person on its register, and the register already carries it per person
// (`BeneficialOwnerView.ficaStatus`). So the screen states it per person, and
// derives every summary from those same rows rather than from a second source.
//
// ABSENT IS NOT VERIFIED. `ficaStatus` is optional and a row that carries no
// status has had nothing verified, so it counts as outstanding. Reading absence
// as clean is how "in good standing" happened in the first place.

type FicaState = 'received' | 'outstanding' | 'rejected';

interface FicaPerson {
  id: string;
  name: string;
  /** The deed word for this person, already mapped. Never a raw category. */
  role: string;
  state: FicaState;
  // ── Manual FICA capture (RULING 2026-09-08, Paul) ──────────────────────────
  // Where a received status came from, carried per person so the row can say
  // it. A firm's own attestation and an identity provider's verification are
  // different facts and the screen has to be able to tell them apart.
  source?: 'manual' | 'vendor';
  confirmedByName?: string;
  confirmedAt?: string;
  /** A manual check the firm withdrew. The person is outstanding again, and
   *  the row says a correction was made rather than pretending nothing ever
   *  happened. */
  withdrawnAt?: string;
  withdrawnByName?: string;
  withdrawnReason?: string;
}

/** The register's stored category → the word a client reads. The deed names a
 *  FOUNDER; `settlor` stays the wire value and never reaches the DOM. */
const FICA_ROLE_LABEL: Record<BoCategory, string> = {
  settlor: 'Founder',
  trustee: 'Trustee',
  beneficiary: 'Beneficiary',
  protector: 'Protector',
  other: 'On the register',
};

/** Plural of each role word, for the summary sentence. */
const FICA_ROLE_PLURAL: Record<BoCategory, string> = {
  settlor: 'founders',
  trustee: 'trustees',
  beneficiary: 'beneficiaries',
  protector: 'protectors',
  other: 'others on the register',
};

const FICA_ROLE_SINGULAR: Record<BoCategory, string> = {
  settlor: 'founder',
  trustee: 'trustee',
  beneficiary: 'beneficiary',
  protector: 'protector',
  other: 'other on the register',
};

/** Register order, so the checklist reads down the deed the way the deed reads. */
const FICA_ROLE_ORDER: BoCategory[] = ['settlor', 'trustee', 'beneficiary', 'protector', 'other'];

function ficaStateOf(owner: { ficaStatus?: 'pending' | 'verified' | 'rejected' }): FicaState {
  if (owner.ficaStatus === 'verified') return 'received';
  if (owner.ficaStatus === 'rejected') return 'rejected';
  return 'outstanding';
}

/** "a", "a and b", "a, b and c" — no serial comma, no dashes. */
function andList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]!}`;
}

interface FicaFile {
  people: FicaPerson[];
  outstanding: number;
  received: number;
  /** "1 founder, 2 trustees and 3 beneficiaries" — empty when nothing is out. */
  outstandingPhrase: string;
  /** True when NOBODY on the register has ever been verified. The periodic
   *  review clock is meaningless before an initial verification exists
   *  (TRS-03), so the screen stops printing one. */
  noInitialFica: boolean;
}

function buildFicaFile(owners: readonly BeneficialOwnerView[]): FicaFile {
  const people: FicaPerson[] = [];
  const outstandingByRole = new Map<BoCategory, number>();
  let received = 0;
  let outstanding = 0;

  for (const category of FICA_ROLE_ORDER) {
    for (const owner of owners) {
      if (owner.category !== category) continue;
      const state = ficaStateOf(owner);
      if (state === 'received') received += 1;
      else {
        outstanding += 1;
        outstandingByRole.set(category, (outstandingByRole.get(category) ?? 0) + 1);
      }
      people.push({
        id: owner.id,
        name: displayPersonName(owner.fullName),
        role: FICA_ROLE_LABEL[category],
        state,
        ...(owner.ficaSource !== undefined && { source: owner.ficaSource }),
        ...(owner.ficaConfirmedByName !== undefined && { confirmedByName: displayPersonName(owner.ficaConfirmedByName) }),
        ...(owner.ficaConfirmedAt !== undefined && { confirmedAt: owner.ficaConfirmedAt }),
        ...(owner.ficaWithdrawnAt !== undefined && { withdrawnAt: owner.ficaWithdrawnAt }),
        ...(owner.ficaWithdrawnByName !== undefined && { withdrawnByName: displayPersonName(owner.ficaWithdrawnByName) }),
        ...(owner.ficaWithdrawnReason !== undefined && { withdrawnReason: owner.ficaWithdrawnReason }),
      });
    }
  }

  const phrase = andList(
    FICA_ROLE_ORDER.filter((c) => (outstandingByRole.get(c) ?? 0) > 0).map((c) => {
      const n = outstandingByRole.get(c)!;
      return `${n} ${n === 1 ? FICA_ROLE_SINGULAR[c] : FICA_ROLE_PLURAL[c]}`;
    }),
  );

  return {
    people,
    outstanding,
    received,
    outstandingPhrase: phrase,
    // RULING 2026-09-15 (Paul): THE WHOLE REGISTER. This was `received === 0`,
    // so one verified person of several read as an initial FICA on file and the
    // biennial clock started over an incomplete statutory file. That is the
    // threshold the round-6 grader failed on the wire. An empty register is
    // outstanding too (grader, 5 Sep 2026: the trust with no people at all was
    // still shown "FICA review due 02 Oct 2026"). The rule lives in
    // `lib/fica-review.ts` beside the reader of the server's own flag, so the
    // screen and the server cannot drift apart again.
    noInitialFica: initialFicaOutstandingOnRegister({ verified: received, total: people.length }),
  };
}

const FICA_STATE_COPY: Record<FicaState, { label: string; bg: string; fg: string }> = {
  received: { label: 'Received', bg: 'var(--positive-soft)', fg: 'var(--positive)' },
  outstanding: { label: 'Outstanding', bg: 'var(--surface-2)', fg: 'var(--ink-2)' },
  rejected: { label: 'Rejected', bg: 'var(--negative-soft)', fg: 'var(--negative)' },
};

/** "12 September 2026", or nothing when the timestamp is not a date. */
function ficaStampDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * The line under a person's name that says where their status came from.
 *
 * Design gate E2: a legal fact on screen is only what the record proves. A
 * manual check says who at the firm signed it and when; a vendor verification
 * says so in different words; and a person nobody has checked says exactly
 * that. None of the three is allowed to read like either of the others.
 */
function ficaProvenanceLine(p: FicaPerson): string | null {
  if (p.state === 'received') {
    const day = ficaStampDate(p.confirmedAt);
    if (p.source === 'manual') {
      const who = p.confirmedByName ? `by ${displayPersonName(p.confirmedByName)}` : 'by this firm';
      return day
        ? `Recorded ${who} on ${day}, from documents the firm has seen.`
        : `Recorded ${who}, from documents the firm has seen.`;
    }
    if (p.source === 'vendor') return 'Verified by an identity provider.';
    // NO SOURCE ON THE RECORD, SO NO CLAIM ABOUT ONE. Rows verified before
    // 8 September 2026 carry no `ficaSource`, and "verified by an identity
    // provider" over one of those would be the screen asserting something the
    // record does not hold. The status pill still says Received; this line
    // stays silent rather than inventing a provenance.
    return null;
  }
  if (p.withdrawnAt) {
    const day = ficaStampDate(p.withdrawnAt);
    const who = p.withdrawnByName ? ` by ${displayPersonName(p.withdrawnByName)}` : '';
    const why = p.withdrawnReason ? `: ${p.withdrawnReason}` : '';
    return day
      ? `An earlier check was withdrawn${who} on ${day}${why}`
      : `An earlier check was withdrawn${who}${why}`;
  }
  return null;
}

/**
 * What to say when the BENEFICIAL OWNERSHIP register holds nobody.
 *
 * NEW-TRUST-OVERVIEW-REGISTER-CONTRADICTION (round 11): this read "Nobody is on
 * the trust register yet", directly under a Stakeholders card listing three
 * people. Both were true of different registers, and the sentence named the
 * wrong one. The FICA card reads the beneficial ownership (BO) register, the
 * register of captured persons each FICA check is recorded against; the people
 * on the trust are on its ROSTER (`lib/trust-roster.ts`). A trust made by the
 * setup commit has people on the roster and nobody on the BO register, so the
 * true sentence names both, and what the gap costs: there is no one to record
 * a check against until they are captured.
 *
 * `rosterPeople` is null while the roster is unread, so the sentence then says
 * only what the BO register proves.
 */
function boRegisterEmptySentence(rosterPeople: number | null): string {
  if (rosterPeople === null) {
    return 'Nobody has been captured on the beneficial ownership register yet, so no FICA verification has been recorded';
  }
  if (rosterPeople === 0) {
    return 'Nobody is recorded on this trust’s roster or its beneficial ownership register yet, so no FICA verification has been recorded';
  }
  return (
    `${rosterPeople} ${rosterPeople === 1 ? 'person is' : 'people are'} on this trust’s roster, ` +
    'but none has been captured on the beneficial ownership register yet, so no FICA verification can be recorded against them'
  );
}

function FicaFileCard({
  file,
  isLoading,
  isError,
  rosterPeople,
  canRecord,
  onRecord,
  onWithdraw,
}: {
  file: FicaFile;
  isLoading: boolean;
  isError: boolean;
  /** Distinct people on the trust's roster (the People tab's count), or null
   *  while it is unread. Only used to make the empty state true. */
  rosterPeople: number | null;
  /** True when this chair may put a FICA check on file. Mirrors the backend
   *  `isTenantManager` gate, so a role that would be refused never sees the
   *  control at all rather than being offered it and 403'd. */
  canRecord: boolean;
  onRecord: (p: FicaPerson) => void;
  onWithdraw: (p: FicaPerson) => void;
}) {
  return (
    <div className="card" style={{ marginTop: 24 }} data-testid="fica-file-card">
      <div className="card-head">
        <div>
          <div className="eyebrow">On file</div>
          <h2 className="card-title">FICA verification</h2>
        </div>
        {!isLoading && !isError && file.people.length > 0 ? (
          <span className="card-meta" data-testid="fica-file-count">
            {file.received} of {file.people.length} received
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading the beneficial ownership register…</p>
      ) : isError ? (
        /* A failed read is not a clean file. Say which one it is. */
        <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>
          The beneficial ownership register is unavailable right now, so FICA status cannot be shown.
        </p>
      ) : file.people.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13 }} data-testid="fica-file-empty">
          {boRegisterEmptySentence(rosterPeople)}. Each person appears here, with a FICA check to
          record, once they are captured on that register.
        </p>
      ) : (
        <>
          <p
            data-testid="fica-file-summary"
            style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5, margin: '0 0 4px' }}
          >
            {file.outstanding === 0
              ? 'FICA documents are on file for everyone on the register.'
              : `FICA documents still outstanding for ${file.outstandingPhrase}.`}
          </p>
          <div>
            {file.people.map((p, i) => {
              const copy = FICA_STATE_COPY[p.state];
              const provenance = ficaProvenanceLine(p);
              return (
                <div
                  key={p.id}
                  data-testid={`fica-person-${p.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom:
                      i === file.people.length - 1 ? 'none' : '1px solid var(--hairline)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--ink)' }}>{displayPersonName(p.name)}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{p.role}</div>
                    {provenance && (
                      <div
                        data-testid={`fica-provenance-${p.id}`}
                        style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 3 }}
                      >
                        {provenance}
                      </div>
                    )}
                  </div>
                  {/* The door. It sits on the row whose status it changes, so
                      the next step is where the reader is already looking
                      (design gate B1 / B3). */}
                  {canRecord && p.state !== 'received' && (
                    <button
                      type="button"
                      className="card-link"
                      data-testid={`fica-record-${p.id}`}
                      onClick={() => onRecord(p)}
                      title={`Record the FICA check your firm has done for ${displayPersonName(p.name)}.`}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: 12,
                        color: 'var(--primary-700)',
                        flexShrink: 0,
                      }}
                    >
                      Record check
                    </button>
                  )}
                  {canRecord && p.state === 'received' && p.source === 'manual' && (
                    <button
                      type="button"
                      className="card-link"
                      data-testid={`fica-withdraw-${p.id}`}
                      onClick={() => onWithdraw(p)}
                      title={`Withdraw the FICA check recorded for ${displayPersonName(p.name)}.`}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: 12,
                        color: 'var(--ink-3)',
                        flexShrink: 0,
                      }}
                    >
                      Withdraw
                    </button>
                  )}
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: copy.bg,
                      color: copy.fg,
                      letterSpacing: '0.01em',
                      flexShrink: 0,
                    }}
                  >
                    {copy.label}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

interface FactProps {
  label: string;
  value: string;
  mono?: boolean;
}

function Fact({ label, value, mono }: FactProps) {
  return (
    <div>
      <div
        className="eyebrow ink"
        style={{ marginBottom: 4 }}
      >
        {label}
      </div>
      <div
        className={mono ? 'mono' : ''}
        style={{ fontSize: mono ? 14 : 15, color: 'var(--ink)', lineHeight: 1.35 }}
      >
        {value}
      </div>
    </div>
  );
}

interface StakeBlockProps {
  count: number;
  label: string;
  sub: string;
  last?: boolean;
  /** Beno, 2026-07-30 ("Don't work"): this row drew an arrow-right icon inside a
   *  plain div — no link, no handler. He clicked it and nothing happened. The
   *  arrow now renders ONLY when there is somewhere to go, so the affordance can
   *  never lie again; rows without a destination are plainly static. */
  to?: '/app/trusts/$id/people' | '/app/trusts/$id/beneficiaries';
  trustId?: string;
}

function StakeBlock({ count, label, sub, last, to, trustId }: StakeBlockProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '12px 0',
        borderBottom: last ? 'none' : '1px solid var(--hairline)',
      }}
    >
      {/* Supporting weight (2026-06-24): the portfolio value in the hero is the
          dominant number now — stakeholder counts step back. */}
      <div
        style={{
          fontFamily: 'var(--serif)',
          fontWeight: 300,
          fontSize: 22,
          fontVariantNumeric: 'tabular-nums lining-nums',
          color: 'var(--ink-2)',
          lineHeight: 1,
          minWidth: 28,
        }}
      >
        {count}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{sub}</div>
      </div>
      {to && trustId ? (
        <Link
          to={to}
          params={{ id: trustId }}
          aria-label={`View ${label.toLowerCase()}`}
          style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--ink-3)' }}
        >
          <Icon name="arrow-right" size={16} color="var(--ink-3)" />
        </Link>
      ) : null}
    </div>
  );
}

interface OutstandingProps {
  icon: 'sign' | 'shield' | 'doc';
  title: string;
  sub: string;
  last?: boolean;
  /** Present only for an informational (never-blocking) row that deep-links
   *  into a tab — e.g. a field-conflict review item. The existing
   *  resolutions/FICA rows leave this unset and stay plain (non-clickable). */
  to?: string;
}

function Outstanding({ icon, title, sub, last, to }: OutstandingProps) {
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '14px 0',
    borderBottom: last ? 'none' : '1px solid rgba(132,99,47,0.18)',
    ...(to ? { textDecoration: 'none', color: 'inherit' } : {}),
  };
  const content = (
    <>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'var(--accent-100)',
          color: 'var(--accent-700)',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={16} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.35 }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--accent-700)', marginTop: 2 }}>{sub}</div>
      </div>
    </>
  );
  if (to) {
    return (
      <Link to={to as never} style={rowStyle}>
        {content}
      </Link>
    );
  }
  return <div style={rowStyle}>{content}</div>;
}

/** dossier-depth-20260711: the tab a `TrustReviewItem.linkTarget` deep-links
 *  to. Built as a full path string (mirrors `trust-tabs.tsx`'s `as never`
 *  idiom) since the target tab is a runtime value, not a route literal the
 *  typed router can check at compile time. `'overview'` resolves to the
 *  current page (no dedicated tab — the trust facts card lives here). */
function reviewItemHref(trustId: string, linkTarget: string): string {
  switch (linkTarget) {
    case 'people':
      return `/app/trusts/${trustId}/people`;
    case 'properties':
      return `/app/trusts/${trustId}/properties`;
    case 'assets':
      return `/app/trusts/${trustId}/assets`;
    case 'documents':
      return `/app/trusts/${trustId}/documents`;
    default:
      return `/app/trusts/${trustId}`;
  }
}

/** One row in the read-only "Registered companies" card — a company
 *  recognised from CIPC documents (a different data source from the
 *  holdings-based Companies tab; see that card's eyebrow). */
function CompanyRow({
  trustId,
  company,
  last,
}: {
  trustId: string;
  company: CompanyView;
  last?: boolean;
}) {
  return (
    <Link
      to="/app/trusts/$id/companies/$companyId"
      params={{ id: trustId, companyId: company.id }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 0',
        borderBottom: last ? 'none' : '1px solid var(--hairline)',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="serif"
          style={{ fontSize: 15, color: 'var(--ink)', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {displayLegalName(company.name)}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
          <span className="mono">{company.cipcRegistration ?? 'None'}</span>
          {company.industry ? ` · ${company.industry}` : ''}
        </div>
      </div>
      <Icon name="arrow-right" size={16} color="var(--ink-3)" />
    </Link>
  );
}

// ── Trust hero — the focal point ────────────────────────────────────────────
// Brand-coloured panel (re-skins per tenant via --primary-*) carrying the
// portfolio value (the dominant figure), a one-glance health line, and the
// headline action. Holds the honest holdings states (loading / error / no
// holdings / valuation-pending / valued) and keeps `data-testid="aum-summary"`
// + the same honest copy the overview tests key on.

function TrustHero({
  trustId,
  trust,
  awaitingCount,
  awaitingUnknown,
  canAct,
  claimedPendingValue,
  ficaOutstandingPhrase,
}: {
  trustId: string;
  /** `totalValueBasis` is the Wave-1 B4 stamp (optional — absent on an old
   *  backend): whether the trust's headline total is an AFS NAV or the live
   *  holdings sum. Rendered as an honest provenance note on the value line. */
  /** TRS-03: `ficaDaysOut` / `nextFicaReview` are ABSENT on a trust with no
   *  initial FICA — the band reads them through `src/lib/fica-review.ts`. */
  trust: {
    ficaDaysOut?: number;
    nextFicaReview?: string;
    initialFicaOnFile?: boolean | null;
    totalValue?: number;
    totalValueBasis?: 'holdings' | 'afs';
  };
  awaitingCount: number;
  /** True when the awaiting-signature query FAILED. A failure must never be
   *  read as "nothing awaiting" — that turns a transient error into a
   *  positive compliance claim (2026-07-19 miss-hunt SCB2C-5). */
  awaitingUnknown: boolean;
  /** When false (a beneficiary-tier member), the "Review & sign" write CTA is
   *  suppressed — they still see the awaiting count in the health line, and get
   *  the read-only "View portfolios" affordance instead. */
  canAct: boolean;
  /** Wave-6 row 4 (Paul-decided): the SECOND figure — claimed-but-not-yet-
   *  registered asset value (whole Rand). Shown as a separate line UNDER the
   *  AUM total; the total itself (holdings-projected) never includes it.
   *  Zero-suppressed — nothing renders when there's nothing pending. */
  claimedPendingValue: number;
  /** "1 founder and 2 trustees", derived from the trust register, when FICA
   *  documents are outstanding and the caller may read the register. Undefined
   *  means UNKNOWN (not readable, still loading, or the read failed) and must
   *  never be treated as "nothing outstanding": the health line simply says
   *  nothing about FICA documents in that case. */
  ficaOutstandingPhrase?: string;
}) {
  const holdingsQ = useDashboardHoldings(trustId);
  const data = holdingsQ.data;
  const holdingsCount = data ? data.byAssetClass.reduce((acc, c) => acc + c.holdings.length, 0) : 0;
  const classCount = data ? data.byAssetClass.length : 0;
  // TOV-02 — ONE source for "assets under administration". This card used to
  // read only the portfolio HOLDINGS feed while the Assets tab read the asset
  // register, so a trust with three recorded assets and no valued holding said
  // "No holdings on record yet" here and "3 assets" one tab over. The number
  // the firm board sums is the trust's own stamped `totalValue`
  // (backend lib/trust-valuation.ts: whole Rand; basis 'holdings' = the live
  // holdings sum, 'afs' = the AFS net-asset-value stands). The card leads with
  // that, so this screen and the firm's board can never disagree; the holdings
  // feed still supplies the breakdown, and the register's count fills the empty
  // state instead of denying the assets exist.
  //
  // Precedence: LIVE valued holdings win (the stamp is recomputed FROM them, so
  // the feed is the fresher copy of the same number); the stamp stands in when
  // it is an AFS net-asset-value (no valued holdings by definition) or when the
  // holdings feed has settled EMPTY. Holdings on record with no opening
  // valuation still read "Valuation pending" — a stamp cannot vouch for them.
  const assetsQ = useTrustAssets(trustId);
  const assetsOnRecord = assetsQ.data?.assets.length ?? 0;
  const holdingsSettled = !holdingsQ.isLoading && !holdingsQ.isError && data !== undefined;
  const stampedRand =
    typeof trust.totalValue === 'number' && trust.totalValue > 0 ? trust.totalValue : null;
  const useStamp =
    stampedRand !== null &&
    (trust.totalValueBasis === 'afs' || (holdingsSettled && holdingsCount === 0));
  const totalValue = useStamp ? stampedRand * 100 : (data?.totalMarketValueZar ?? 0);
  const valued = useStamp || !!data?.asAt;
  const asAtLabel = data?.asAt ? formatDate(data.asAt) : null;
  const topClass =
    data && data.byAssetClass.length > 0
      ? [...data.byAssetClass].sort((a, b) => b.totalMarketValueZar - a.totalMarketValueZar)[0]!
      : null;

  // ── TRS-03 ON THE HERO BAND ────────────────────────────────────────────────
  // "ASSETS UNDER ADMINISTRATION … FICA review due ~1 month" was still printing
  // over a trust whose initial FICA has never been done (grader round 2,
  // 6 September). The firm's screens were fixed in round 1 and this band was
  // not, because it reads the clock off the trust row directly. It now reads it
  // through the one helper, which returns nothing when there is no cycle.
  const reviewDays = ficaDaysOutOf(trust);
  const noInitialFica = isInitialFicaOutstanding(trust);
  const overdue = reviewDays !== undefined && reviewDays <= 0;
  // SCB2C-5: `healthy` may only be claimed when the signature status is
  // actually KNOWN. When the resolutions query failed we cannot distinguish
  // "nothing awaiting" from "we couldn't look" — say so rather than printing
  // a green all-clear over a possible outstanding signature.
  // ── AND THE FICA FILE ITSELF (TRS-01) ──────────────────────────────────────
  // "No signatures or reviews outstanding" printed one card above a checklist
  // naming four people whose FICA documents are missing would be the two-adjacent-
  // fields contradiction all over again, so an outstanding file is stated here
  // and outranks the calm line. `healthy` gains the same condition: a trust
  // missing every identity document is not a trust with nothing outstanding.
  const ficaDocsOutstanding = ficaOutstandingPhrase !== undefined;
  const healthy =
    reviewDays !== undefined &&
    reviewDays > 30 &&
    !noInitialFica &&
    awaitingCount === 0 &&
    !awaitingUnknown &&
    !ficaDocsOutstanding;
  const healthText = noInitialFica
    ? // TRS-03: the outstanding work is the FIRST verification, not a review.
      ficaOutstandingPhrase !== undefined
      ? `Initial FICA outstanding for ${ficaOutstandingPhrase}`
      : 'Initial FICA outstanding'
    : overdue
    ? `FICA review overdue by ${Math.abs(reviewDays!)} days`
    : awaitingCount > 0
    ? `${awaitingCount} item${awaitingCount === 1 ? '' : 's'} ${awaitingCount === 1 ? 'needs' : 'need'} your attention`
    : awaitingUnknown
    ? 'Signature status unavailable right now'
    : ficaOutstandingPhrase !== undefined
    ? `FICA documents outstanding for ${ficaOutstandingPhrase}`
    : healthy
    // 2026-08-06: this used to read "In good standing · nothing overdue", which
    // claimed far more than the condition above tests. `healthy` means the FICA
    // review is more than 30 days out and nothing awaits a signature — it says
    // nothing about SARS filings, which Elreign cannot see. The statutory
    // calendar card below carries those dates. Say only what was checked.
    ? 'No signatures or reviews outstanding'
    : reviewDays !== undefined
    ? `FICA review due ${ficaHuman(reviewDays).toLowerCase()}`
    : // No clock and no "no initial FICA" fact: the review date could not be
      // read. Say that rather than print a countdown or an all-clear.
      'Review date not on file';

  const cream = 'var(--surface)';
  const softCream = 'color-mix(in srgb, var(--surface) 68%, transparent)';
  const dotColor = overdue ? '#E08570' : '#7DC59C';

  return (
    <section
      data-testid="aum-summary"
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, var(--primary-700), var(--primary-800))',
        borderRadius: 22,
        padding: '30px 34px',
        marginBottom: 24,
        display: 'grid',
        gridTemplateColumns: '1.6fr auto',
        gap: 28,
        alignItems: 'center',
        boxShadow: '0 26px 50px -32px color-mix(in srgb, var(--primary-800) 85%, transparent)',
      }}
    >
      <div style={{ position: 'relative', minWidth: 0 }}>
        <div
          className="eyebrow on-dark"
          style={{ marginBottom: 12 }}
        >
          Assets under administration
        </div>

        {!useStamp && holdingsQ.isLoading ? (
          <p style={{ color: softCream, fontSize: 14, margin: 0 }} aria-busy="true">
            Loading portfolio value&hellip;
          </p>
        ) : !useStamp && holdingsQ.isError ? (
          <p style={{ color: softCream, fontSize: 14, margin: 0, lineHeight: 1.5, maxWidth: '46ch' }}>
            Portfolio value is unavailable right now. Your data is safe. See the Portfolios
            tab to retry.
          </p>
        ) : !useStamp && holdingsCount === 0 ? (
          <p style={{ color: softCream, fontSize: 14, margin: 0, lineHeight: 1.5, maxWidth: '48ch' }}>
            {assetsOnRecord > 0
              ? `${assetsOnRecord} asset${assetsOnRecord === 1 ? '' : 's'} on record; see the Assets tab. A value appears here once their valuations are lodged.`
              : 'No holdings on record yet. Asset values appear here once the trust’s opening valuations are lodged.'}
          </p>
        ) : valued ? (
          <>
            <div
              className="serif num"
              // letterSpacing stays `normal`: negative tracking crushes the
              // U+202F thousands separator formatZarCents emits, so the figure
              // renders as "R2510000,00" (Beno, 2026-07-30).
              style={{ fontWeight: 300, fontSize: 52, letterSpacing: 'normal', color: cream, lineHeight: 1 }}
            >
              {formatZarCents(totalValue)}
            </div>
            <div className="num" style={{ color: softCream, fontSize: 13, marginTop: 12 }}>
              {/* F5: the total is a dated, provenanced fact — as-at plus what
                  it reflects when the Wave-1 backend stamps totalValueBasis. */}
              {asAtLabel ? `Valued ${asAtLabel}` : 'Recorded value'}
              {trust.totalValueBasis
                ? trust.totalValueBasis === 'afs'
                  ? ' · AFS valuation'
                  : ' · live holdings sum'
                : ''}{' '}
              · {holdingsCount} holding{holdingsCount === 1 ? '' : 's'} ·{' '}
              {classCount} asset class{classCount === 1 ? '' : 'es'}
              {topClass ? ` · ${assetClassLabel(topClass.assetClass)} largest` : ''}
            </div>
          </>
        ) : (
          <>
            <div
              className="serif"
              style={{ fontWeight: 300, fontStyle: 'italic', fontSize: 30, color: 'var(--accent-300)' }}
            >
              Valuation pending
            </div>
            <div style={{ color: softCream, fontSize: 13, marginTop: 10 }}>
              {holdingsCount} holding{holdingsCount === 1 ? '' : 's'} on record · {classCount} asset
              class{classCount === 1 ? '' : 'es'} · awaiting opening valuation
            </div>
          </>
        )}

        {/* Wave-6 row 4 (Paul-decided): the SECOND figure. "Assets under
            administration" above is the HONEST registered total (holdings-
            projected). This line surfaces claimed-but-not-yet-registered asset
            value SEPARATELY — never folded into the AUM total. Zero-suppressed:
            nothing renders when there's nothing pending. */}
        {claimedPendingValue > 0 ? (
          <div
            data-testid="hero-claimed-pending"
            style={{
              marginTop: 12,
              color: softCream,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.02em',
                padding: '2px 8px',
                borderRadius: 999,
                border: '1px solid color-mix(in srgb, var(--surface) 30%, transparent)',
                color: 'var(--accent-300)',
                whiteSpace: 'nowrap',
              }}
            >
              Pending registration
            </span>
            <span style={{ color: cream }}>
              <b style={{ fontWeight: 600 }}>{formatRand(claimedPendingValue)}</b> claimed, not yet
              registered
            </span>
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            marginTop: 16,
            color: softCream,
            fontSize: 13.5,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: dotColor,
              boxShadow: `0 0 0 4px color-mix(in srgb, ${dotColor} 22%, transparent)`,
              flexShrink: 0,
            }}
          />
          {healthText}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {/* The "Review & sign" affordance is a write CTA (it deep-links into the
            resolutions sign flow) — suppressed for a beneficiary-tier member,
            who still sees the awaiting count in the health line above and gets
            the read-only "View portfolios" link instead. */}
        {canAct && awaitingCount > 0 ? (
          <Link
            to="/app/trusts/$id/resolutions"
            params={{ id: trustId }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--accent-400)',
              color: 'var(--primary-900, #091B17)',
              borderRadius: 12,
              padding: '12px 18px',
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Review &amp; sign: {awaitingCount} awaiting →
          </Link>
        ) : (
          <Link
            to="/app/trusts/$id/portfolios"
            params={{ id: trustId }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'color-mix(in srgb, var(--surface) 12%, transparent)',
              color: cream,
              borderRadius: 12,
              padding: '12px 18px',
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            View portfolios →
          </Link>
        )}
      </div>
    </section>
  );
}

// ── The beneficiary's hero ──────────────────────────────────────────────────
// What replaces "Assets under administration" for a beneficiary-tier member.
//
// The temptation is to render nothing and let the screen start at Trust facts.
// That reads as a page that failed to load its top half. The temptation after
// that is to render the same hero with dashes in it, which is worse — it shows
// a beneficiary the SHAPE of a number they are not being given, and invites the
// question "why is my trust worth —?".
//
// So this says the true thing plainly, in the same slot, at the same weight: you
// are a beneficiary of this trust, here is what that means you can see, and here
// is the way to it. Nothing about the corpus, no apology, no lock icons.
function BeneficiaryHero({ trustId, trustName }: { trustId: string; trustName: string }) {
  const cream = 'var(--surface)';
  const softCream = 'color-mix(in srgb, var(--surface) 68%, transparent)';
  return (
    <section
      data-testid="beneficiary-summary"
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, var(--primary-700), var(--primary-800))',
        borderRadius: 22,
        padding: '30px 34px',
        marginBottom: 24,
        boxShadow: '0 26px 50px -32px color-mix(in srgb, var(--primary-800) 85%, transparent)',
      }}
    >
      <div
        className="eyebrow on-dark"
        style={{ marginBottom: 12 }}
      >
        Your interest
      </div>
      <div
        className="serif"
        style={{ fontWeight: 300, fontSize: 34, color: cream, lineHeight: 1.2, maxWidth: '20ch' }}
      >
        You are a beneficiary of {displayTrustName(trustName)}.
      </div>
      <p style={{ color: softCream, fontSize: 14, marginTop: 14, lineHeight: 1.6, maxWidth: '54ch' }}>
        You can see what this trust has distributed to you and the tax certificates issued in
        your name. The trust&rsquo;s assets, register and governance records are held by its
        trustees.
      </p>
      <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
        <Link
          to="/app/trusts/$id/distributions"
          params={{ id: trustId }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            borderRadius: 999,
            background: cream,
            color: 'var(--primary-800)',
            fontSize: 13.5,
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Your distributions
          <Icon name="arrow-right" size={15} color="var(--primary-800)" />
        </Link>
        <Link
          to="/app/trusts/$id/tax"
          params={{ id: trustId }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            borderRadius: 999,
            border: '1px solid color-mix(in srgb, var(--surface) 40%, transparent)',
            color: cream,
            fontSize: 13.5,
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Your tax certificates
        </Link>
      </div>
    </section>
  );
}

// ── AFS financials panel (handoff B3 — AFS-GRADING findings 1 + 2) ─────────
// The confirmed annual-financial-statements record for this trust, projected
// by trusts-get.ts on the SAME `financials` surface as the hero above (a
// beneficiary never reads the corpus's confirmed figures either). Renders the
// record's key figures with their own "as at" date — never `lastUpdated`,
// which is when the row last recalculated, not when the statements were
// dated (the exact bug the dashboard hero's AFS provenance note fixed).
//
// HONESTY RULE: nothing renders when the trust has no confirmed AFS on file
// (`afsFinancials` null/absent) — no empty-scaffold card promising figures
// that don't exist. The call site checks presence before mounting this at
// all; the per-row filter below additionally never prints a row for a field
// the statements didn't carry (undefined, not 0 — a real R0 total DOES print).
function AfsFinancialsCard({ afs }: { afs: NonNullable<TrustSummary['afsFinancials']> }) {
  // Same "month + year" granularity + same helper the dashboard hero uses for
  // this exact field (`afsFinancials.periodEndDate` → `totalValueAsAt`) — one
  // honest as-at rendering for one fact, everywhere it appears.
  const asAt = afs.periodEndDate ? formatAsAtShort(afs.periodEndDate) : null;
  const rows: Array<{ label: string; value: number }> = [
    { label: 'Net asset value', value: afs.netAssetValue },
    { label: 'Total assets', value: afs.totalAssets },
    { label: 'Total liabilities', value: afs.totalLiabilities },
    { label: 'Total income', value: afs.totalIncome },
    { label: 'Total expenses', value: afs.totalExpenses },
    { label: 'Distributed to beneficiaries', value: afs.distributionsToBeneficiaries },
  ].filter((r): r is { label: string; value: number } => r.value !== undefined);

  // Nothing on the record but a period end date (rare — a confirm that found
  // no readable figures at all): still nothing worth a card of empty rows.
  if (rows.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 24 }} data-testid="afs-financials-card">
      <div className="card-head">
        <div>
          <div className="eyebrow">From the annual financial statements</div>
          <h2 className="card-title">Confirmed AFS figures</h2>
        </div>
        {asAt ? <span className="card-meta">as at {asAt}</span> : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 32px' }}>
        {rows.map((r) => (
          <Fact key={r.label} label={r.label} value={formatRand(r.value)} />
        ))}
      </div>
    </div>
  );
}

function TrustOverviewScreen() {
  const { id } = useParams({ from: '/app/trusts/$id/' });

  const trustQ = useTrust(id);
  // Tier-gate the actionable sign CTA in the hero: a beneficiary-tier member
  // sees the awaiting status but not the click-to-sign affordance.
  const canAct = useCanActOnTrust(id);

  // ── What this caller may READ of this trust (2026-08-18) ───────────────────
  // Tiered read access shipped across 37 handlers; this screen had not caught
  // up, so a beneficiary-tier member met a 403 on nearly every panel. Each
  // query below is now gated on the surface its endpoint serves, so the panels
  // they cannot read are never REQUESTED — not requested and then failed. That
  // matters twice over: no wall of red, and no burst of 403s in the network log
  // masking a real one.
  //
  // A beneficiary is left with a coherent, smaller screen: the fact of the
  // trust, their own distributions, and their own compliance. Not a broken one.
  const { can } = useTrustSurfaces(id);
  const canFinancials = can('financials');
  const canPeople = can('people');
  const canGovernance = can('governance');
  const canCompliance = can('compliance');

  // Wave-6 row 4 (Paul-decided): the SECOND figure for the hero. The per-trust
  // detail endpoint (useTrust) doesn't carry claimedPendingValue; the trusts
  // LIST does (backend trusts-list.ts). Reuse it (it's a common cached query
  // reached via the "My trusts" nav) to look this trust's pending sum up —
  // no new endpoint, no per-trust asset fan-out.
  //
  // The list now OMITS `claimedPendingValue` for a caller who may not read
  // `financials`, so this resolves to undefined for a beneficiary and the line
  // zero-suppresses itself even before the hero is hidden.
  const trustsListQ = useTrusts();
  const claimedPendingValue =
    trustsListQ.data?.trusts.find((t) => t.id === id)?.claimedPendingValue ?? 0;
  // The BO register: the FICA card's source, and ONLY the FICA card's.
  const boQ = useBeneficialOwnership(id, { enabled: canPeople });
  // THE TRUST'S PEOPLE, from the People tab's own two reads (the deed roster and
  // the beneficiary register) through the same function (`buildTrustRoster`).
  // Stakeholders used to read the BO register and fall back to the TRUSTEE list,
  // which has no founder in it; see `lib/trust-roster.ts`.
  const membersQ = useTrustMembers(canPeople ? id : undefined);
  const beneficiariesQ = useTrustBeneficiaries({ trustId: id, enabled: canPeople });
  const auditQ = useTrustAuditLog({ trustId: id, limit: 5, enabled: canGovernance });
  const roleLabel = useRoleLabel();
  const awaitingQ = useResolutions({ trustId: id, status: 'awaiting', enabled: canGovernance });
  // Read-only "Registered companies" card — CIPC-recognised companies
  // (a different data source from the holdings-based Companies tab).
  // Tenant-wide (no trustId filter): recognised companies are usually
  // standalone rather than trust-held, and in the own-tenant client model
  // tenant-wide === this client.
  const companiesQ = useCompanies({ enabled: canFinancials });

  // ── Manual FICA capture (RULING 2026-09-08, Paul) ──────────────────────────
  // The gate is the OPERATOR's own role, not the role a minted act-as token
  // carries inside the subject tenant: a `manage` grant mints 'admin' in the
  // client tenant, so the subject role cannot tell a firm standing in from a
  // client standing on its own feet. The call itself rides the operator session
  // for the same reason lodge-master does (see `useRecordFicaCheck`).
  const acting = useActingContext();
  const canRecordFica = canRecordFicaCheck(acting.operator.role);
  const [ficaRecordTarget, setFicaRecordTarget] = useState<FicaPerson | null>(null);
  const [ficaWithdrawTarget, setFicaWithdrawTarget] = useState<FicaPerson | null>(null);

  if (trustQ.isLoading) {
    return (
      <ScreenShell activeNav="trusts" eyebrow="The trust" title="Loading…" sub="">
        <p style={{ color: 'var(--ink-3)' }}>Loading trust…</p>
      </ScreenShell>
    );
  }

  if (trustQ.isError || !trustQ.data) {
    return (
      <ScreenShell activeNav="trusts" eyebrow="The trust" title="Trust not available" sub="">
        <div className="card" style={{ padding: 24, maxWidth: 600 }}>
          <p style={{ color: 'var(--negative)', marginBottom: 6 }}>Trust record not available.</p>
          {trustQ.error && (
            <p style={{ fontSize: 12, color: 'var(--ink-4)' }}>{mapError(trustQ.error, 'load')}</p>
          )}
        </div>
      </ScreenShell>
    );
  }

  const trust = trustQ.data.trust;
  const owners = boQ.data?.beneficialOwners ?? [];
  // One derivation of the FICA position, read by the facts card, the hero health
  // line and the checklist below. A second source for the same number is a bug
  // generator (2026-08-12), and this number is a compliance claim.
  const ficaFile = buildFicaFile(owners);
  // The register is the `people` surface. A caller who cannot read it gets no
  // FICA statement at all, in either direction.
  const ficaKnown = canPeople && !boQ.isLoading && !boQ.isError;
  // TRS-03: the server's own answer to "is there a review cycle at all". It is
  // read through the shared helper so this screen, the dashboard and the firm
  // console cannot disagree, and so a missing clock is never defaulted to a
  // number. `undefined` = no cycle (or unreadable), never "due today".
  const reviewDaysOut = ficaDaysOutOf(trust);
  const reviewDate = ficaReviewDateOf(trust);
  const registration = registrationDisplay(trust.registration, trust.registrationDate);

  // STAKEHOLDERS = THE ROSTER, as the People tab reads it (round 11,
  // NEW-TRUST-OVERVIEW-REGISTER-CONTRADICTION). One source: the same two reads
  // and the same `buildTrustRoster`, so a band here and a band there are the
  // same list, and the headcount is the People tab's own phrase.
  const beneficiaryRegisterKnown = !beneficiariesQ.isLoading && !beneficiariesQ.isError;
  const roster = buildTrustRoster(membersQ.data?.members ?? [], beneficiariesQ.data?.beneficiaries, {
    beneficiaryRegisterKnown,
  });
  const rosterKnown = !membersQ.isLoading && !membersQ.isError && membersQ.data !== undefined;
  // 2026-07-30: the name line under each row is a PREVIEW, and says so ("+2
  // more"), one name for trustees and residual roles, three otherwise.
  const NAMES_SHOWN: Record<RosterRoleCategory, number | undefined> = {
    founder: undefined,
    trustee: 1,
    beneficiary: undefined,
    other: 1,
  };
  const stakes: Array<{ cat: RosterRoleCategory; count: number; label: string; sub: string }> =
    ROLE_GROUP_ORDER.filter((cat) => roster.bands[cat].length > 0).map((cat) => {
      const entries = roster.bands[cat];
      return {
        cat,
        count: entries.length,
        label: ROLE_GROUP_LABEL[cat](entries.length),
        sub: nameList(entries.map((e) => displayPersonName(e.name)), NAMES_SHOWN[cat]),
      };
    });
  // QA 4 Oct 2026, P2-19: people the BO register holds who are not on the
  // roster (a per-document confirm seeds trustees as pending, not appointed,
  // but writes their BO rows). The FICA card below lists them, so Stakeholders
  // must not read as if nobody were recorded: it names them, as what they are.
  // Only once the register has actually been read.
  const registerOnly = ficaKnown ? registerOnlyPeople(roster, owners) : [];
  const stakesCountPhrase = [
    ...rosterCountPhrasesOf(roster),
    ...(registerOnly.length > 0 ? [`${registerOnly.length} not yet on the roster`] : []),
  ].join(' · ');

  const recentActivity = auditQ.data?.audit ?? [];

  const companies = companiesQ.data?.companies ?? [];

  const awaiting = awaitingQ.data?.resolutions ?? [];
  const outstandingActions: OutstandingProps[] = [];
  if (awaiting.length > 0) {
    const first = awaiting[0]!;
    outstandingActions.push({
      icon: 'sign',
      title: `${awaiting.length} resolution${awaiting.length === 1 ? '' : 's'} awaiting signatures`,
      sub: first.title,
    });
  }
  // ── A REVIEW CLOCK ON A TRUST THAT WAS NEVER VERIFIED (TRS-03) ─────────────
  // Beno, 22 August, reading "FICA review due 21 Sept 2026 · biennial cycle" off
  // a trust with no FICA documents at all: a PERIODIC review is the thing you do
  // AFTER an initial verification, and this one had never happened. The date is
  // a default the backend stamps 30 days out at creation
  // (`backend/src/lib/build-trust-doc.ts`), so a cycle was being reported that
  // nothing started.
  //
  // When the register is readable and NOBODY on it has been verified, the
  // outstanding item is the initial verification, named person by person. The
  // periodic clock only appears once at least one verification exists.
  if (ficaKnown && ficaFile.noInitialFica) {
    outstandingActions.push({
      icon: 'shield',
      // An empty register is nobody verified too (grader, 5 Sep 2026); it has no
      // names to list, so the sentence stands on its own.
      title: ficaFile.people.length > 0
        ? `FICA verification outstanding for ${ficaFile.outstandingPhrase}`
        : 'FICA verification outstanding',
      // Say the work already done rather than claiming none of it exists: with
      // the whole-register rule a partly verified trust reaches this branch, and
      // "No FICA documents are on file" would be flatly untrue of it.
      sub: ficaFile.people.length === 0
        ? boRegisterEmptySentence(rosterKnown ? roster.peopleCount : null)
        : (ficaVerifiedPhrase({ verified: ficaFile.received, total: ficaFile.people.length })
            ?? 'No FICA documents are on file for this trust yet'),
    });
  } else if (isInitialFicaOutstanding(trust)) {
    // TRS-03, the same fact from the SERVER's register read rather than this
    // screen's own. It fires when the register query has not settled (or is not
    // readable here) but the trust row already carries `initialFicaOnFile:
    // false`, so the countdown branch below can never be reached on a trust
    // with no first verification.
    outstandingActions.push({
      icon: 'shield',
      title: 'FICA verification outstanding',
      sub: 'No FICA verification has been recorded for this trust yet',
    });
  } else if (reviewDaysOut !== undefined && reviewDaysOut <= 30) {
    outstandingActions.push({
      icon: 'shield',
      title:
        reviewDaysOut <= 0
          ? `FICA review overdue (${Math.abs(reviewDaysOut)} days)`
          : `FICA review due ${formatDate(reviewDate ?? null)}`,
      sub:
        reviewDaysOut <= 0
          ? 'Action required immediately'
          : `In ${reviewDaysOut} days · risk-based cycle`,
    });
  }
  // dossier-depth-20260711: PII-clean "documents disagreed on X" rows from the
  // reconcile commit — informational, never a blocking task (the authoritative
  // value already stands). Each row deep-links into the field's tab.
  for (const item of trust.reviewItems ?? []) {
    // ELR-12: a distinct kind for "we could not tell who the account holder
    // is", never the field-conflict copy — nothing was kept here, because
    // nothing matched. See `lib/api/manifest-types.ts`.
    if (item.kind === 'identity-unmatched') {
      outstandingActions.push({
        icon: 'doc',
        title: 'Could not confirm who the account holder is',
        // Both ways this row can be raised, and no more than that. It fires when
        // SEVERAL people in the pack match the account owner (a father and a son
        // share a name) and when NONE of them do. Naming only the second was
        // true when this copy was written and stopped being true the moment an
        // ambiguous match started refusing the writeback as well.
        sub:
          'More than one person in the pack could be you, or none of them ' +
          "matched the account owner. The owner's profile was left unchanged. " +
          'Tap to review.',
        to: reviewItemHref(id, item.linkTarget),
      });
      continue;
    }
    outstandingActions.push({
      icon: 'doc',
      title: `Documents disagreed on ${item.fieldLabel}`,
      sub: 'We kept the authoritative value. Tap to review.',
      to: reviewItemHref(id, item.linkTarget),
    });
  }

  // Deed date shows once now (in the facts card), so the sub-line no longer
  // repeats it. "in good standing" removed 2026-08-22 (meeting ruling): a
  // FICA-review countdown over 30 days says nothing about whether the FICA
  // documents themselves exist — trusts with every ID outstanding carried the
  // phrase. State the countdown; make no compliance claim.
  //
  // The registration goes through the same dictionary the facts card uses: this
  // line printed the backend's raw "Pending registration" placeholder under the
  // trust's own name, one of the three places the same fact was rendered.
  const subParts: string[] = [registration.text];
  // TRS-03: no periodic countdown before an initial verification exists — the
  // server withholds the clock, and this screen's own register read is the
  // second guard.
  if (
    reviewDaysOut !== undefined &&
    reviewDaysOut > 0 &&
    !(ficaKnown && ficaFile.noInitialFica)
  ) {
    subParts.push(`FICA review in ${reviewDaysOut}d`);
  }
  const sub = subParts.join(' · ');

  return (
    <ScreenShell
      activeNav="trusts"
      eyebrow="The trust"
      title={<em>{displayTrustName(trust.name)}</em>}
      sub={sub}
    >
      <TrustTabs trustId={id} activeTab="overview" />

      {/* The hero IS the financials surface — assets under administration,
          holdings, valuation provenance. A beneficiary reads none of it: the
          corpus is not theirs until it is distributed. Hidden entirely rather
          than shown empty, because "R0" and "no holdings on record" are both
          false statements about a trust that may hold millions. */}
      {canFinancials ? (
        <TrustHero
          trustId={id}
          trust={trust}
          awaitingCount={awaiting.length}
          awaitingUnknown={awaitingQ.isError}
          canAct={canAct}
          // Undefined unless we positively KNOW documents are outstanding. An
          // unreadable register is not a clean one.
          ficaOutstandingPhrase={
            ficaKnown && ficaFile.outstanding > 0 ? ficaFile.outstandingPhrase : undefined
          }
          claimedPendingValue={claimedPendingValue}
        />
      ) : (
        <BeneficiaryHero trustId={id} trustName={trust.name} />
      )}

      {/* AFS-GRADING findings 1 + 2 (handoff B3) — the confirmed AFS record,
          when the trust has one. `financials`-gated like the hero above.
          AfsFinancialsCard's own guard additionally renders nothing when the
          record carries no readable figures at all. */}
      {canFinancials && trust.afsFinancials ? (
        <AfsFinancialsCard afs={trust.afsFinancials} />
      ) : null}

      {/* What the deed says about how beneficiaries receive (Beno, 2026-08-01).
          FLAGGED OFF BY DEFAULT. The extraction abstains correctly under unit
          test, but no real model has yet been run against a stack of real
          deeds — and being confidently wrong about a distribution rule is worse
          than saying nothing, which is Beno's own rule. Flip
          VITE_TRUST_KEY_FACTS_LIVE=true once that eval has been run. */}
      {isTrustKeyFactsLive() ? (
        <TrustKeyFacts deedDistribution={trust.deedDistribution ?? null} />
      ) : null}

      <div
        style={{
          display: 'grid',
          // The Stakeholders card is the `people` surface. When it is hidden the
          // Trust facts card takes the full width rather than sitting in a
          // 1.4fr column beside a gap — a lopsided empty half reads as a
          // failed load, which is the impression this whole change exists to
          // avoid.
          gridTemplateColumns: canPeople ? '1.4fr 1fr' : '1fr',
          gap: 24,
        }}
      >
        <div className="card" data-testid="trust-facts-card">
          <div className="card-head">
            <div>
              <div className="eyebrow">Trust facts</div>
              <h2 className="card-title">On the deed</h2>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 32px' }}>
            <Fact label="Trust name" value={displayTrustName(trust.name)} />
            {/* Beno's walkthrough (2026-07-14 S26): "Where did this come from?
                Must this not be 'Trust Registration No'?" — was labelled
                "Registration" and showed the literal placeholder string for a
                trust the backend hasn't recognised a Master/IT number for yet.
                Renamed the label; the value itself was already honest (the
                real number when known, the placeholder only when genuinely
                absent) — only the mono/code styling is conditional now, so a
                real IT number reads as code but the placeholder reads as prose. */}
            {/* ── THESE TWO FIELDS CONTRADICTED EACH OTHER (2026-08-26) ────────
                A frame of the 25 August walkthrough shows, side by side:

                  TRUST REGISTRATION NO   Pending registration
                  REGISTRATION DATE       29 Jan 2013

                Both cannot be true. A registration DATE comes off the Master's
                Letter of Authority, and the Master does not date a letter for a
                trust that is not registered — so a date on file is proof the
                trust IS registered and only its NUMBER has not been read yet.
                "Pending registration" asserted the opposite, on the screen a
                fiduciary shows a client.

                Beno's standing rule (2026-08-01): a fact not in the documents
                must not show. The corollary is the one that bit here — a fact
                the documents DO carry must not be contradicted either. So the
                placeholder now says what is actually true of each case, and the
                date decides which case we are in.

                ── AND THE FIX OVERSHOT (2026-08-27) ─────────────────────────
                The 26 August version answered "Not yet registered" whenever no
                Letter of Authority was on file. That is the same false claim
                pointing the other way: a trust registered in 2013 whose letter
                nobody has uploaded yet was told it is not registered. Nothing
                on file is not evidence of absence. The mapping now lives in
                `lib/format/registration` and reports the state of the FILE,
                because the "My trusts" card prints the very same fact and had
                been left printing the raw placeholder all along. */}
            <Fact
              label="Trust registration no"
              value={registration.text}
              mono={registration.isNumber}
            />
            <Fact label="Deed date" value={formatDate(trust.deedDate)} />
            {/* Sourced from the Letter of Authority (distinct from the deed
                date — Beno's deed 20 Mar 2000 vs LoA registration 29 Mar
                2000); the API omits it when no LoA date is known. */}
            <Fact
              label="Registration date"
              value={trust.registrationDate ? formatDate(trust.registrationDate) : 'Not on file'}
            />
            {/* "Last updated" removed 2026-08-26 (TOV-04). Paul had both date
                stamps taken off the firm dashboard on 23 August, on his SECOND
                ask — "AI for the second time remove this date and time" — and
                this one survived that sweep because the sweep fixed the screen
                it was shown rather than the pattern. His reasoning applies here
                unchanged: "less information is better if it's not load-bearing",
                and a viewer's own clock already tells them today's date. The
                portal's "as at" stamp is a different thing and stays: it
                qualifies a VALUATION, which is load-bearing. */}
            {/* ── A CYCLE NOBODY EVER STARTED (TRS-03) ─────────────────────────
                "FICA review due 21 Sept 2026 · biennial cycle" ran on trusts
                that had never had a single identity verified. A periodic review
                is what follows an initial verification; the date itself is a
                default the backend stamps 30 days after creation. Where the
                register is readable and shows nobody verified, these two fields
                would be reporting a cycle that has not begun, so the card states
                the position instead and the checklist below names each person. */}
            {(ficaKnown && ficaFile.noInitialFica) || isInitialFicaOutstanding(trust) ? (
              /* Whole register (ruling 2026-09-15): a trust part of the way
                 through says how far, so the firm sees its own progress instead
                 of a flat "none" it knows to be wrong. */
              <Fact
                label="FICA verification"
                value={
                  (ficaKnown
                    ? ficaVerifiedPhrase({ verified: ficaFile.received, total: ficaFile.people.length })
                    : undefined) ?? 'None on file yet'
                }
              />
            ) : reviewDaysOut === undefined ? (
              // The clock could not be read. Neither field is invented.
              <Fact label="Next FICA review" value="Not on file" />
            ) : (
              <>
                <Fact label="Next FICA review" value={formatDate(reviewDate ?? null)} />
                <Fact label="Time to review" value={ficaHuman(reviewDaysOut)} />
              </>
            )}
          </div>
        </div>

        {/* `people` — who else benefits from a family trust, and in what
            proportion, is the single most sensitive thing in it. */}
        {canPeople ? (
        <div className="card" data-testid="stakeholders-card">
          <div className="card-head">
            <div>
              <div className="eyebrow">In place</div>
              <h2 className="card-title">Stakeholders</h2>
            </div>
            {rosterKnown ? (
              <span className="card-meta" data-testid="stakeholders-count">
                {stakesCountPhrase}
              </span>
            ) : null}
          </div>
          {membersQ.isLoading || beneficiariesQ.isLoading ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>Loading…</p>
          ) : membersQ.isError ? (
            /* A failed read is not an empty roster. */
            <p style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>
              The trust&rsquo;s roster is unavailable right now, so its people cannot be shown.
            </p>
          ) : stakes.length === 0 && registerOnly.length === 0 ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>
              No founder, trustee or beneficiary is recorded on this trust&rsquo;s roster yet.
            </p>
          ) : (
            <>
            {stakes.length === 0 ? (
              <p
                data-testid="stakeholders-roster-empty"
                style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}
              >
                Nobody is on this trust&rsquo;s roster yet. The people below are captured on its
                beneficial ownership register, which the FICA card reads.
              </p>
            ) : null}
            {stakes.map((s, i) => (
              <StakeBlock
                key={s.cat}
                count={s.count}
                label={s.label}
                sub={s.sub}
                last={i === stakes.length - 1 && registerOnly.length === 0}
                // Founders + trustees live on People & access; beneficiaries
                // have their own register. Any other row stays static (and so
                // shows no arrow) rather than promising a page that isn't there.
                //
                // 2026-08-01: the 2026-07-30 Founder-vocabulary rename (label
                // above: 'Founder'/'Founders', was 'Settlor'/'Settlors') and
                // this regex landed in the same merge but the regex was never
                // updated — it kept testing for the OLD label, so a Founder
                // row silently lost its arrow/link the same day Beno's own
                // "the arrow now renders ONLY when there is somewhere to go"
                // fix (above, on StakeBlockProps.to) shipped. Re-broken by the
                // rename, not by that fix. See index.test.tsx for the pin.
                //
                // Round 11: the destination is keyed on the BAND, not on the
                // label text, which is singular or plural by count and was
                // already missed once by a label rename. Every roster band has a
                // real destination: beneficiaries their register, the rest
                // People & access, where the same bands are drawn.
                {...(s.cat === 'beneficiary'
                  ? { to: '/app/trusts/$id/beneficiaries', trustId: id }
                  : { to: '/app/trusts/$id/people', trustId: id })}
              />
            ))}
            {registerOnly.length > 0 ? (
              <div data-testid="stakeholders-register-only">
                <StakeBlock
                  count={registerOnly.length}
                  label="Captured, not yet on the roster"
                  sub={nameList(registerOnly.map((p) => displayPersonName(p.fullName)), 3)}
                  last
                />
              </div>
            ) : null}
            </>
          )}
        </div>
        ) : null}
      </div>

      {/* WHOSE FICA IS ON FILE, PERSON BY PERSON (TRS-01 · TRS-02).
          `people` — the register is the source, and a caller who cannot read it
          is told nothing about FICA rather than being told it is fine. */}
      {canPeople ? (
        <>
          <FicaFileCard
            file={ficaFile}
            isLoading={boQ.isLoading}
            isError={boQ.isError}
            rosterPeople={rosterKnown ? roster.peopleCount : null}
            canRecord={canRecordFica}
            onRecord={setFicaRecordTarget}
            onWithdraw={setFicaWithdrawTarget}
          />
          {/* Mounted only while a person is chosen. That is not a micro
              optimisation: it is what makes each dialog a FRESH one, so last
              person's ticks can never carry into the next person's record. */}
          {ficaRecordTarget && (
            <RecordFicaCheckDialog
              open
              onClose={() => setFicaRecordTarget(null)}
              trustId={id}
              subject={ficaRecordTarget}
            />
          )}
          {ficaWithdrawTarget && (
            <WithdrawFicaCheckDialog
              open
              onClose={() => setFicaWithdrawTarget(null)}
              trustId={id}
              subject={ficaWithdrawTarget}
            />
          )}
        </>
      ) : null}

      {/* `governance` (activity / resolutions) + `compliance` (the FICA review
          row). Trustee deliberations and the trust's compliance posture are the
          administration file; a beneficiary's own compliance lives on their own
          screens, not here. Both cards go together — a lone "Action required"
          card with only FICA in it would tell a beneficiary about a review they
          cannot act on. */}
      {canGovernance ? (
      <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
        <div className="card">
          <div className="card-head">
            <div>
              <div className="eyebrow">On the record</div>
              <h2 className="card-title">Recent activity</h2>
            </div>
            <Link
              to="/app/trusts/$id/audit"
              params={{ id }}
              style={{ fontSize: 13, color: 'var(--primary-700)', textDecoration: 'none', fontWeight: 500 }}
            >
              Audit log →
            </Link>
          </div>
          {auditQ.isLoading ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</p>
          ) : recentActivity.length === 0 ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>No recent activity on record.</p>
          ) : (
            <div className="activity-list">
              {recentActivity.map((e) => (
                <div key={e.id} className="activity-item">
                  <div
                    className={'avatar ' + avatarColorOf(e.performedByRole || 'system')}
                    style={{ width: 36, height: 36, fontSize: 12 }}
                  >
                    {initialsOf(roleLabel(e.performedByRole || 'system'))}
                  </div>
                  <div>
                    {/* The role's title and the event in words, the dashboard
                        feed's rendering (NEW-R12-ACTIVITY-RAW-EVENT printed
                        "admin setup.committed" here). */}
                    <div className="activity-body">
                      <b>{roleLabel(e.performedByRole || 'system')}</b>{' '}
                      <span style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{labelForEvent(e.action)}</span>
                    </div>
                    <div className="activity-time">{formatTime(e.ts)}</div>
                  </div>
                  {labelForEventSubject(e.subjectType) ? (
                    <div className="activity-pill">{labelForEventSubject(e.subjectType)}</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ background: 'var(--accent-50)', borderColor: 'var(--accent-300)' }}>
          <div className="card-head">
            <div>
              <div className="eyebrow" style={{ color: 'var(--accent-700)' }}>
                Outstanding
              </div>
              <h2 className="card-title">Action required</h2>
            </div>
          </div>
          {outstandingActions.length === 0 ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>Nothing on the action list right now.</p>
          ) : (
            outstandingActions.map((a, i) => (
              <Outstanding
                key={i}
                icon={a.icon}
                title={a.title}
                sub={a.sub}
                to={a.to}
                last={i === outstandingActions.length - 1}
              />
            ))
          )}
        </div>
      </div>
      ) : null}

      {/* The trust's SARS year. The hero above reports only what Elreign can
          observe (FICA + signatures); these are the statutory dates it cannot.
          FICA is passed in so the two appear in one ordered list.
          `compliance` — the TRUST's filing obligations belong to its trustees,
          who carry them. A beneficiary's own tax paperwork is the Tax tab. */}
      {canCompliance ? (
        <div style={{ marginTop: 24 }}>
          {/* TRS-03: a trust with no initial FICA on file has no review CYCLE to
              clock; the calendar must not list a periodic review for it. */}
          <StatutoryCalendarCard
            ficaReviewOn={calendarFicaReviewOn({
              ficaKnown,
              noInitialFica: ficaFile.noInitialFica || isInitialFicaOutstanding(trust),
              nextFicaReview: reviewDate,
            })}
            count={4}
          />
        </div>
      ) : null}

      {/* `financials` — trust-held companies are part of the corpus. */}
      {canFinancials ? (
      <div className="card" style={{ marginTop: 24 }} data-testid="registered-companies-card">
        <div className="card-head">
          <div>
            <div className="eyebrow">From CIPC documents</div>
            <h2 className="card-title">Registered companies</h2>
          </div>
          {companies.length > 0 && (
            <span className="card-meta">
              {companies.length} {companies.length === 1 ? 'company' : 'companies'}
            </span>
          )}
        </div>
        {companiesQ.isLoading ? (
          <p style={{ color: 'var(--ink-3)', fontSize: 13 }} aria-busy="true">
            Loading companies&hellip;
          </p>
        ) : companiesQ.isError ? (
          <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>
            Company records unavailable right now. Your data is safe.
          </p>
        ) : companies.length === 0 ? (
          <p style={{ color: 'var(--ink-3)', fontSize: 13 }}>
            No CIPC-registered companies recognised yet. They appear here as the
            trust&rsquo;s CIPC documents (CoR14/CoR15/CIPC certificates) are processed.
          </p>
        ) : (
          companies.map((c, i) => (
            <CompanyRow key={c.id} trustId={id} company={c} last={i === companies.length - 1} />
          ))
        )}
      </div>
      ) : null}
    </ScreenShell>
  );
}
